import { faBuildings, faEyeSlash, faGlobe, faUserGroup } from '@fortawesome/pro-regular-svg-icons';
import { GlobalPermission, SharingDocumentPermissionLevel, SharingPermissions, WorkspaceDocument } from 'scribe-web-shared/types/document';
import { Organization } from 'scribe-web-shared/types/organization';
import { IconDefinition } from 'stylus-ui/Icon';

const DOCUMENT_WRITE_PERMISSION_LEVELS: readonly SharingDocumentPermissionLevel[] = ['write', 'owner'];

function isWritePermission(permission: SharingDocumentPermissionLevel | undefined): boolean {
  return DOCUMENT_WRITE_PERMISSION_LEVELS.includes(permission);
}

const PRIVATE_GLOBAL_PERMISSIONS: readonly GlobalPermission[] = ['private', 'unknown'];

function isPrivateGlobalPermission(permission: GlobalPermission | undefined): boolean {
  return PRIVATE_GLOBAL_PERMISSIONS.includes(permission);
}

/**
 * Checks if two global permission levels are both private, or both shared.
 */
export function haveSameVisibility(gp1: GlobalPermission, gp2: GlobalPermission): boolean {
  return isPrivateGlobalPermission(gp1) === isPrivateGlobalPermission(gp2);
}

/**
 * For sufficient permissions, suggest an appropriate shared global permission
 * level that a document should have after copying its link.
 *
 * Returns `undefined` if modifying the global permission level is not possible
 * based on the given permissions, or if the current shared global permission
 * level is already appropriate.
 */
export const suggestGlobalPermissionAfterCopyLink = (
  document: WorkspaceDocument | undefined,
  permissions: SharingPermissions | undefined,
  sharedWithIndividuals: boolean
): GlobalPermission | undefined => {
  const permissionLevelWithoutIndividualSharing = permissions?.permission_level_without_individual_sharing;

  if (!isWritePermission(permissionLevelWithoutIndividualSharing)) {
    return;
  }

  const globalPermission = document?.global_permission;

  if (permissions?.owned_by_enterprise) {
    if (isPrivateGlobalPermission(globalPermission) && !sharedWithIndividuals) {
      // Suggest "Shared with Team" for private enterprise docs.
      return 'team';
    }
  } else if (!['link_read', 'team'].includes(globalPermission)) {
    // Suggest "Shareable with Link" for bottoms-up documents.
    return 'link_read';
  }

  return globalPermission;
};

export type PermissionMenuItem = {
  id: number;
  title: string;
  description: string;
  enabled: boolean;
  permissionLevel: GlobalPermission;
  icon: IconDefinition;
};

//  SCRIBE DOCUMENT PERMISSION LEVEL MENU ITEMS
export const activeTeamPermissionMenuItems = (
  sharedWithIndividuals: boolean,
  permissions: SharingPermissions,
  organizationOwner: Organization | null = null
): PermissionMenuItem[] => {
  const privatelySharedText = 'Only people invited can access';
  const teamSharedText = `${organizationOwner?.name || 'Team'} can access`;
  const organizationSharedText = `${organizationOwner?.super_organization?.name || 'Organization'} can access`;
  const linkSharedText = 'Anyone with the link can view';

  return [
    {
      id: 1,
      title: sharedWithIndividuals ? privatelySharedText : 'Not shared',
      description: sharedWithIndividuals ? privatelySharedText : 'Not shared',
      enabled: permissions?.visible_global_settings?.private,
      permissionLevel: 'private',
      icon: faEyeSlash
    },
    {
      id: 2,
      title: 'Team',
      description: teamSharedText,
      enabled: permissions?.visible_global_settings?.team,
      permissionLevel: 'team',
      icon: faUserGroup
    },
    {
      id: 3,
      title: 'Organization',
      description: organizationSharedText,
      enabled: permissions?.visible_global_settings?.super_org_read,
      permissionLevel: 'super_org_read',
      icon: faBuildings
    },
    {
      id: 4,
      title: 'Shareable',
      description: linkSharedText,
      enabled: permissions?.visible_global_settings?.link_read,
      permissionLevel: 'link_read',
      icon: faGlobe
    }
  ];
};
